import angular from 'angular';
import d3 from 'd3';
import wellknown from '../../../lib/wellknown';
import App from '../../app';

const AMPLITUDE_EVENT_NAME = "Pageview: Map";
export default function MapMainController(
  GeozoneService,
  usersService,
  $scope,
  $window,
  $rootScope,
  $state,
  $stateParams,
  $compile,
  $timeout,
  $location,
  mapService,
  equipmentRepository,
  localStorageService,
  gettextCatalog,
  $q,
) {
  'ngInject';

  const self = this; // to maintain reference in closures

  // Local storage keys for saving map position and zoom
  const KEY_MAP_CENTER = 'map_center';
  const KEY_MAP_ZOOM = 'map_zoom';

  // Local storage key for equipment object
  const KEY_EQUIPMENT = 'equipment';

  const REFRESH_TIMEOUT_MINS = 5;

  // cluster marker properties
  const CLUSTER_MAX_RADIUS = 80;

  const MAP_MOVE_DEBOUNCE_TIME = 1500; // in (ms)

  const MAP_DEFAULT_LOCATION = [40.737, -73.923]; // NYC
  const MAP_DEFAULT_ZOOM = 3;

  /* Used to check which map is being used. */
  const KEY_LOCALSTOREAGE_MAP_BASELAYER = 'baselayer';

  const addToDataLayer = (data) => window.dataLayer.push(data);

  // State variables
  let isUpdatingPins = false;

  let geozoneFeatureGroup;
  let geozoneFeatureGroupLabels;
  let markersLayer;

  // Loads the starting position for the map
  function getInitialPosition() {
    return new Promise((resolve) => {
      // Attempt to retrieve the last saved position from localStorage
      let mapCenter = null;
      try {
        mapCenter = JSON.parse(localStorageService.get(KEY_MAP_CENTER));
      } catch (err) {}

      // If the position was successfully retrieved from localStorage, exit the function
      if (mapCenter !== null) {
        resolve(mapCenter);
        return;
      }

      // Try and get the user's geolocation
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve([position.coords.latitude, position.coords.longitude]),
          () => resolve(MAP_DEFAULT_LOCATION),
        );
      } else {
        resolve(MAP_DEFAULT_LOCATION);
      }
    });
  }

  self.$onInit = function() {
    App.amplitude.track(AMPLITUDE_EVENT_NAME);
  }

  // Create the map object
  self.initMap = function (position, zoom) {
    if (self.map) {
      return;
    }

    // Set the default options for hte map
    let options = {
      zoomControl: false,
      worldCopyJump: true,
      center: position || MAP_DEFAULT_LOCATION,
      zoom: zoom || MAP_DEFAULT_ZOOM,
    };

    // Commented out until the weather control plugin fixes https issue
    // Feature flag the weather and time dimension control
    if (usersService.hasRequiredFeatures(['map.weather'])) {
      // use flag 'map.weather' to add weather control to map
      options = _.merge(options, {
        weatherEnabled: true,
        timeDimension: true,
        timeDimensionControl: true, // adds timed dimension control to the map control. set true to add control
        timeDimensionOptions: {
          period: 'PT1M', //one minute duration
        },
        timeDimensionControlOptions: {
          position: 'bottomright',
          loopButton: true,
          autoPlay: true,
          playerOptions: {
            buffer: 10,
            transitionTime: 250,
            loop: true,
          },
          timeSliderDragUpdate: true, //update the map on slide move
        },
      });
    }

    self.map = mapService.createMapDiv('map', options); // options will add the time dimension control
    self.map.setMaxBounds([
      [-85, -2000],
      [85, 2000],
    ]); //world view so user doesnt lose the map

    // Initialize the geozone feature group, but don't add it to the map
    geozoneFeatureGroup = L.featureGroup();
    geozoneFeatureGroupLabels = L.featureGroup();

    // This is the clustered markers layer
    markersLayer = new L.MarkerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: CLUSTER_MAX_RADIUS,
      iconCreateFunction: mapService.defineClusterIcon, // this is where the magic happens for graph icons.
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
    });

    // add the clusters to map
    self.map.addLayer(markersLayer);

    // variables to make sure the api is not called many time when moving around the map
    $scope.oldBounds = {};
    $scope.updateFlag = true;
    // Set the list panel to be offscreen by default
    $scope.panelOffscreen = true;

    // make sure there is no filtering on load
    mapService.setFacets({});
    // For asset type filtering, tags on the service need to be kept.
    //mapService.setTagFilters({});

    const overlayControl = mapService.getControlLayer();

    if (usersService.hasRequiredFeatures(['geozones'])) {
      overlayControl.addOverlay(geozoneFeatureGroup, gettextCatalog.getString('Geozones'));
      overlayControl.addOverlay(
        geozoneFeatureGroupLabels,
        gettextCatalog.getString('Geozone Labels'),
      );
    }

    // MAP EVENTS
    self.map.on('load', function () {
      self.refreshPins(false);
    });

    self.map.on('locationfound', function () {
      // Once we've got a position get data and set the updated date.
      $rootScope.$broadcast('map-locationfound');
    });

    self.map.on('locationerror', function () {
      $rootScope.$broadcast('map-locationerror');
    });

    self.map.on('moveend', function () {
      const bounds = self.map.getBounds();
      mapService.setCurrentBounds(bounds);
      self.storeBounds();

      if ($scope.updateFlag) {
        if (JSON.stringify($scope.oldBounds) !== JSON.stringify(bounds)) {
          // Find new local assets...
          $rootScope.$broadcast('map-moveend', {
            bounds: self.map.getBounds(),
          });
          mapService.requestedCursor = 0;
          updateOnMapMove();
          $scope.oldBounds = bounds;
        }
      }

      addToDataLayer({
        event: 'mapMoved',
      });
    });

    self.map.on('layerremove', function () {});

    self.map.on('layeradd', function (event) {
      if (event.layer === geozoneFeatureGroup || event.layer === geozoneFeatureGroupLabels) {
        self.refreshGeozones();
      }

      const currentMap = localStorageService.get(KEY_LOCALSTOREAGE_MAP_BASELAYER);

      switch (currentMap) {
        case 'satellite':
          $('.asset-icon').removeClass('asset-icon-streets').addClass('asset-icon-satellite');
          break;
        case 'streets':
          $('.asset-icon').removeClass('asset-icon-satellite').addClass('asset-icon-streets');
          break;
      }
    });

    let zoomLevel;

    self.map.on('zoomend', function () {
      const currentZoom = self.map.getZoom();

      if (currentZoom !== zoomLevel) {
        addToDataLayer({
          event: 'mapZoomed',
          data: {
            originalZoomLevel: zoomLevel,
            currentZoomLevel: currentZoom,
          },
        });
      }

      $rootScope.$broadcast('map-zoomend');
    });

    self.map.on('zoomstart', function () {
      zoomLevel = self.map.getZoom();
      $rootScope.$broadcast('map-zoomstart');
    });

    self.map.on('popupopen', function () {
      const popupItems = document.getElementsByClassName('popupItem');

      for (const item of popupItems) {
        item.addEventListener('click', () => {
          const lat = parseFloat(item.attributes.lat.value);
          const lng = parseFloat(item.attributes.lng.value);

          const BOUNDS_OFF_SET = 0.0005;

          const nwBounds = L.latLng(lat + BOUNDS_OFF_SET, lng - BOUNDS_OFF_SET);
          const seBounds = L.latLng(lat - BOUNDS_OFF_SET, lng + BOUNDS_OFF_SET);

          const bounds = L.latLngBounds(nwBounds, seBounds);

          self.map.fitBounds(bounds);

          $state.go('map.equipment.detail', {
            equipmentId: item.id,
          });

          addToDataLayer({
            event: 'clickItemInAssetListPopup',
          });
        });
      }

      $rootScope.$broadcast('map-popupopen', {
        map: self.map,
      });
    });

    self.map.on('popupclose', function () {
      L.openedPopup = null;
    });

    self.map.on('click', function (e) {
      if ($window.innerWidth > 900) {
        L.openedPopup = null;
        $rootScope.$broadcast('map-click');
        mapService.requestedEquipmentId = null;
        $state.go('map.equipment');
      }

      addToDataLayer({
        event: 'clickOnMap',
      });
    });

    self.map.on('baselayerchange', function (event) {
      const layerName = event.name.toLowerCase();
      localStorageService.set(KEY_LOCALSTOREAGE_MAP_BASELAYER, layerName);

      addToDataLayer({
        event: 'mapBaseLayerChanged',
        data: {
          layerName,
        },
      });
    });

    const getSelectedOverlays = () => {
      const selectedOverlays = [];
      // This is not cool, but there isn't a map api call to get selected overlays
      $('.leaflet-control-layers-overlays .leaflet-control-layers-selector:checked')
        .next()
        .each((idx, el) => {
          selectedOverlays.push(el.textContent.trim());
        });

      $('.leaflet-control-weather-overlays .leaflet-control-weather-selector:checked')
        .next()
        .each((idx, el) => {
          selectedOverlays.push(el.textContent.trim());
        });

      return selectedOverlays;
    };

    self.map.on('overlayadd', (e) => {
      addToDataLayer({
        event: 'mapOverlayChanged',
        data: {
          overlayName: e.name,
          selected: true,
          currentOverlays: getSelectedOverlays(),
        },
      });
    });

    self.map.on('overlayremove', (e) => {
      addToDataLayer({
        event: 'mapOverlayChanged',
        data: {
          overlayName: e.name,
          selected: false,
          currentOverlays: getSelectedOverlays(),
        },
      });
    });
  };

  // keep track of bounds
  self.storeBounds = function () {
    const center = self.map.getCenter();
    const zoom = self.map.getZoom();
    if (center) {
      localStorageService.set(KEY_MAP_CENTER, JSON.stringify(center));
    }
    if (zoom) {
      localStorageService.set(KEY_MAP_ZOOM, zoom);
    }
  };

  const defineFeature = function (feature, latlng) {
    const result = mapService.defineMarkerFeature(feature, latlng, $scope);

    result.on('click', function (e) {
      const { latlng = {} } = e;
      const currentZoomLevel = self.map.getZoom();
      const newPosition = new L.LatLng(latlng.lat, latlng.lng);
      self.map.flyTo(newPosition, currentZoomLevel, { animation: true });

      $state.go('map.equipment.detail', {
        equipmentId: e.target.feature.properties.id,
      });

      addToDataLayer({
        event: 'clickAssetOnMap',
      });
    });

    result.on(
      'mouseover',
      function () {
        const isDetailspopupOpen = L.openedPopup;
        // ignore if the hover popup opened already or details popup opened
        if ((result._popup && result._popup.isOpen()) || isDetailspopupOpen) {
          return;
        }

        // make sure to update hovered asset scope as its been binded to the hoverPopupComponent first then open the popup
        $q((resolve) => {
          $scope.hoveredAsset = feature.properties;
          resolve();
        }).then(() => {
          const popupOptions = {
            minWidth: 350,
            closeButton: true,
            autoClose: true,
            autoPan: false,
          };

          result.bindPopup(self.hoverPopupComponent, popupOptions);
          result.openPopup();

          result.getPopup().on('remove', () => {
            addToDataLayer({
              event: 'assetPopupClosed',
            });
          });
        });

        addToDataLayer({
          event: 'showAssetPopupOnMap',
        });
      },
      { passive: true },
    );

    return result;
  };
  // Refresh the equipment data
  const refreshMapData = function (equipmentList, fitBoundsFlag) {
    if (self.map.hasLayer(geozoneFeatureGroup) || self.map.hasLayer(geozoneFeatureGroupLabels)) {
      self.refreshGeozones();
    }

    markersLayer.clearLayers();

    markersLayer.addLayer(
      L.geoJson(mapService.getEquipmentGeoJson(equipmentList), {
        pointToLayer: defineFeature,
      }),
    );

    if (fitBoundsFlag) {
      $scope.updateFlag = false; // do not want to update the map after fitting bounds
      if (
        markersLayer.getBounds().getSouthWest() !== undefined ||
        markersLayer.getBounds().getNorthEast() !== undefined
      ) {
        self.map.fitBounds(
          [
            markersLayer.getBounds().getSouthWest().wrap(),
            markersLayer.getBounds().getNorthEast().wrap(),
          ],
          5,
        );
      }

      // because there is no way to tell when fitBounds has finished, the flag used to tell the map to update on move (moveend)
      // must be put into a timeout since there is no other way to ensure fitBounds has finished but this isnt guaranteed
      $timeout(function () {
        $scope.updateFlag = true;
      }, 700); // .7 s
    } else {
      $scope.firstLoad = false;
    }
  };

  let isUpdatingGeozones = false;

  let isGeozonesDirty = false;

  self.refreshGeozones = function () {
    if (isUpdatingGeozones) {
      isGeozonesDirty = true;
      return;
    }

    isGeozonesDirty = false;
    isUpdatingGeozones = true;

    GeozoneService.getAllGeozones()
      .then(
        function (response) {
          mapService.setVisibleGeozones(response.data);

          geozoneFeatureGroup.clearLayers();
          geozoneFeatureGroupLabels.clearLayers();

          angular.forEach(mapService.visibleGeozones, function (geozone) {
            if (geozone.geometry) {
              // Parse the geozone geometry to well known text
              const geoJsonGeometry = wellknown.parse(geozone.geometry);

              if (geoJsonGeometry) {
                const geoJsonData = {
                  type: 'Feature',
                  geometry: geoJsonGeometry,
                  properties: {
                    name: `${geozone.name}`,
                  },
                  geozone,
                };

                const geoJsonDataLabel = {
                  type: 'Feature',
                  geometry: geoJsonGeometry,
                  properties: {
                    name: `${geozone.name}`,
                  },
                  geozone,
                };
                const hideGeometry = {
                  opacity: 0,
                  fillOpacity: 0,
                };

                const geoJson = L.geoJson(geoJsonData).on('click', function () {
                  $location.path(`/alerts/geozone/detail/${geozone.id}`);
                });
                const geoJsonLabels = L.geoJson(geoJsonDataLabel, {
                  style: hideGeometry,
                })
                  .bindTooltip(
                    function (layer) {
                      return layer.feature.properties.name;
                    },
                    {
                      direction: 'center',
                      permanent: true,
                      interactive: true,
                      className: 'labelstyle',
                    },
                  )
                  .on('click', function () {
                    $location.path(`/alerts/geozone/detail/${geozone.id}`);
                  });

                geoJson.addTo(geozoneFeatureGroup);
                geoJsonLabels.addTo(geozoneFeatureGroupLabels);
              }
            }
          });

          if (isGeozonesDirty) {
            self.refreshGeozones();
          }
        },
        function () {},
      )
      .finally(function () {
        isUpdatingGeozones = false;
      });
  };

  $scope.$on('assets-onupdate', function (event, payload) {
    const inBoundsSearch = payload && payload.searchMode === mapService.SEARCH_MODE.IN_MAP_BOUNDARY;
    self.refreshPins(inBoundsSearch);
  });
  // Called when updated asset detail (this probably shouldn't be here any more and could probalby be replaced with a watch on mapservice
  $scope.$on('asset-onupdate', function () {
    const equipment = mapService.selectedEquipment;
    self.showPopupForEquipment(equipment);
  });

  // On selected page in the list panel
  $scope.$on('selected-page', function () {
    self.refreshPins(false);
  });

  // Does a query for equipment on the map
  self.refreshPins = function (mapMoved) {
    if (isUpdatingPins) {
      return;
    }

    isUpdatingPins = true;

    const cursor = mapService.requestedCursor;

    mapService.loading = true;
    $scope.errorFlag = false;

    mapService.setCurrentBounds(self.map.getBounds());

    let currBounds = mapService.getApiBounds();

    if (self.map.getZoom() < 3) {
      currBounds = null;
    }
    const modes = [];
    if (!mapMoved) {
      modes.push('searchOutsideBounds');
    }

    const includes = ['images'];

    equipmentRepository
      .getEquipmentSearch(
        mapService.queryString || '*',
        {
          limit: mapService.getLimit(),
          since: cursor,
        },
        mapService.filters,
        currBounds,
        null,
        mapService.facets,
        mapService.tagFilters,
        modes,
        includes,
      )
      .then(
        function (response) {
          isUpdatingPins = false;
          mapService.setEquipmentResponseData(response);
          mapService.cursor = cursor;
          $scope.$broadcast('mapservice-updated');

          refreshMapData(response.data.hits, response.data.locationRedirectFlag);

          // Collect data for analytics
          if (response.data.hits !== undefined) {
            const facets = {};

            if (mapService.facets !== undefined) {
              const facetAbstractionProperties = [
                { name: 'alerts', property: 'name' },
                { name: 'maintenance', property: 'name' },
                { name: 'securityGroups', property: 'name' },
                { name: 'lastCommunicated', property: 'label' },
                { name: 'lastSeen', property: 'range' },
              ];

              facetAbstractionProperties.forEach((facetProperty) => {
                if (mapService.facets[facetProperty.name] !== undefined) {
                  const facet = mapService.facets[facetProperty.name]
                    .filter((item) => item._selected === true)
                    .map((item) => item[facetProperty.property]);

                  if (facet.length > 0) facets[facetProperty.name] = facet;
                }
              });
            }

            addToDataLayer({
              event: 'assetsSearching',
              data: {
                keyword: mapService.queryString,
                since: cursor,
                bounds: currBounds,
                facets,
                attributes: mapService.tagFilters,
                totalLimitedMapAssets: response.data.hits.length,
                totalMapAssets: response.data.total,
              },
            });
          }
        },
        function (error) {
          let errorMsg = gettextCatalog.getString('An error occurred while retrieving asset data');
          if (error.data || error.status) {
            if (error.data.message) {
              errorMsg = `${error.data.message} ${error.status.toString()}`;
            } else {
              errorMsg += `: ${error.status.toString()}`;
            }
          } else {
            errorMsg += gettextCatalog.getString('. Please try again later.');
          }
          $scope.$broadcast('equip-search-error', {
            text: errorMsg,
          });
        },
      )
      .finally(function () {
        isUpdatingPins = false;
        mapService.loading = false;
      });
  };

  $scope.autorefreshTimeout = null;

  function autoRefresh() {
    // if not on the map page cancel the timer  -note: change $location.path() from $location.url() so timer does not stop when adding search obj to url
    if ($location.path() !== '/map') {
      return false;
    }
    $scope.autorefreshTimeout = $timeout(function () {
      self.refreshPins(false);
    }, 1000 * 60 * REFRESH_TIMEOUT_MINS); // 5mins
  }

  $scope.$on('$destroy', function () {
    if ($scope.autorefreshTimeout) {
      $timeout.cancel($scope.autorefreshTimeout);
    }
  });

  self.initRefreshTimer = function () {
    $scope.autorefreshTimeout = $timeout(function () {
      autoRefresh();
    }, 1000 * 60 * REFRESH_TIMEOUT_MINS); // 5mins
  };

  function updateOnMapMove() {
    if ($scope.mapRefreshTimeoutPromise) {
      $timeout.cancel($scope.mapRefreshTimeoutPromise);
    }

    $scope.mapRefreshTimeoutPromise = $timeout(function () {
      self.refreshPins(true);
    }, MAP_MOVE_DEBOUNCE_TIME);
  }

  // apends pin that will only appear on mobile to the pin
  self.appendPinToPopup = function (popup) {
    const width = 50;

    const halfWidth = width / 2;

    const height = 60;

    const svgContainer = d3
      .select(popup._container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', '0 0 50 60')
      .style('margin-top', `${-height / 2 - 15}px`) // height + size of pin
      .style('margin-left', `${-halfWidth}px`)
      .style('position', 'absolute')
      .classed('mobile-pin', true);

    const g = svgContainer.append('g').attr('transform', `translate(${0},${0})`);

    const strokeWidth = 2;

    const bezierLine = d3.svg
      .line()
      .x(function (d) {
        return d[0];
      })
      .y(function (d) {
        return d[1];
      })
      .interpolate('basis-closed');

    const points = [
      [halfWidth, height],
      [width, height * 0.3],
      [halfWidth, 0],
      [0, height * 0.3],
      [halfWidth, height],
    ];

    const path = g
      .append('path')
      .attr('d', bezierLine(points))
      .attr('stroke', 'white')
      .attr('stroke-width', strokeWidth)
      .attr('fill', '#000');

    const filter = path.append('filter').attr('id', 'drop-shadow').attr('height', '130%');

    filter
      .append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 5)
      .attr('result', 'blur');

    filter
      .append('feOffset')
      .attr('in', 'blur')
      .attr('dx', 5)
      .attr('dy', 5)
      .attr('result', 'offsetBlur');

    const feMerge = filter.append('feMerge');

    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const circleContainer = svgContainer.append('g').attr('stroke', 'white').attr('fill', 'white');

    const circle = circleContainer.append('circle').attr('id', 'centerCircle');

    circle
      .attr('cx', '25')
      .attr('cy', '22')
      .attr('r', '5')
      .attr('stroke', '#fff')
      .attr('stroke-width', '3')
      .attr('fill', '#fff');

    // Adding equipment object to local storage.
    localStorageService.set(KEY_EQUIPMENT, popup.equipment);
  };

  // PUBLIC FUNCTIONS
  self.showPopupForEquipment = function (equipment) {
    // If we don't have the required equipment info, we can't show the popup.
    if (!equipment || !equipment.coords || equipment.coords.length < 2) {
      self.map.closePopup();
      return false;
    }

    // Verify the requested equipment matches the current state. If not, do nothing.
    if ($stateParams.equipmentId !== equipment.id) {
      return false;
    }

    // No need to continue if the popup is already open with the requested equipment.
    if (L.openedPopup && L.openedPopup.equipment && L.openedPopup.equipment.id === equipment.id) {
      return false;
    }

    // let html = '<map-equipment-popup></map-equipment-popup>',
    //   linkFunction = $compile(angular.element(html)),
    //   newScope = $scope.$new();
    // newScope.equipment = equipment;

    const latLng = L.latLng(equipment.coords[1], equipment.coords[0]);

    // http://leafletjs.com/reference.html#popup
    // bind openedPopup to the L varible which is conveniently located in the gloabal namespace.
    // we will use this in the quickview controller to make it fit when we expand
    const popupWidth = 0;

    const popupOptions = {
      minWidth: popupWidth,
      closeButton: false,
      minHeight: 500,
      offset: L.point(0, 5),
      closeOnClick: false,
      keepInView: false,
      className: 'assetpopup',
      autoPanPadding: L.point(100, 100),
    };

    L.openedPopup = L.popup(popupOptions)
      .setLatLng(latLng)
      // .setContent(linkFunction(newScope)[0]) //
      .openOn(self.map);

    L.openedPopup.equipment = equipment;

    self.appendPinToPopup(L.openedPopup);
    return true;
  };

  $rootScope.$on('$stateChangeStart', function (e, toState, toParams) {
    if (toState.name === 'map.equipment.detail') {
      mapService.setRequestedEquipmentId(toParams.equipmentId);
    } else {
      mapService.setSelectedEquipment(null);
    }
  });

  $rootScope.$on('$stateChangeSuccess', function () {
    // Close the popup if we are not on the equipment page
    if ($state.current.name === 'map.equipment') {
      // Set euqiment to null and trigger and asset update so that the popup will disappear
      mapService.setSelectedEquipment(null);
      $rootScope.$broadcast('asset-onupdate');
      // we don't want to show a popup for equipment or eozones when we are on a list page
      self.map.closePopup();
    } else if ($state.includes('map.equipment.detail')) {
      mapService.requestedEquipmentId = $stateParams.equipmentId;

      const cachedEquipment = _.find(mapService.equipmentResponseData.data.hits, function (e) {
        return e.id === $stateParams.equipmentId;
      });

      if (cachedEquipment) {
        mapService.setSelectedEquipment(cachedEquipment);
        $rootScope.$broadcast('asset-onupdate');
      }
    }
  });

  /**
   * compile map-hover-popup once and bind it to $scope.hoveredAsset
   */
  self.creatMapHoverPopupComponent = () => {
    const popuphtml = `<map-hover-popup asset="hoveredAsset"></map-hover-popup>`;
    const componentScope = $scope.$new();
    self.hoverPopupComponent = $compile(angular.element(popuphtml))(componentScope)[0];
  };
  // Main initialization function
  self.init = function () {
    getInitialPosition().then((pos) => {
      const zoom = localStorageService.get(KEY_MAP_ZOOM) || MAP_DEFAULT_ZOOM;
      self.initMap(pos, zoom);
      // Loading equipment object from local storage and call showPopupForEquipment that renders the Pin
      const keyEquipment = localStorageService.get(KEY_EQUIPMENT);
      if (keyEquipment != null) {
        self.showPopupForEquipment(JSON.parse(JSON.stringify(keyEquipment)));
      }
      self.creatMapHoverPopupComponent();
      // pass true will make the call to get equipment based on map bound then the map will not update the view based on the returnd equipment
      // todo : self.refreshPins() been called in diff spot causing to call api many times,
      //       in some cases it passed fales which cased the map to set bound based on returnd equipment   => need to be cleaned
      self.refreshPins(true);
      self.initRefreshTimer();
    });
  };

  self.init();
}
